<template>
  <div
    :class="inline ? '' : 'offcanvas offcanvas-fullscreen-sm-down offcanvas-end payment-offcanvas'"
    :style="inline ? 'display: contents;' : 'width: 820px; max-width: 100%;'"
    :tabindex="inline ? null : -1"
    ref="offcanvas"
    :data-bs-backdrop="inline ? null : 'static'"
    :data-bs-scroll="inline ? null : 'true'"
  >
    <!-- Header -->
    <div v-if="!inline" class="offcanvas-header bg-theme text-white py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0">
          <i class="fa-solid fa-cash-register me-2" />
          {{ $t('Payment') }}
        </h5>
        <div class="small opacity-75 mt-1">
          {{ billNo }} &nbsp;|&nbsp; {{ sessionName }}
        </div>
      </div>
      <button type="button" class="btn-close btn-close-white" @click="handleClose" />
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-4">

      <!-- Summary Row -->
      <div class="row g-2 mb-4">
        <div class="col-4">
          <div class="card border-0 bg-light rounded-3 h-100">
            <div class="card-body p-3 text-center">
              <div class="text-muted small mb-1">{{ $t('Grand Total') }}</div>
              <h5 class="fw-bold mb-0">{{ $storeIndex.formatThaiCurrency(grandTotal) }}</h5>
            </div>
          </div>
        </div>
        <div class="col-4">
          <div class="card border-0 rounded-3 h-100" :class="paidAmount > 0 ? 'bg-info bg-opacity-10' : 'bg-light'">
            <div class="card-body p-3 text-center">
              <div class="text-muted small mb-1">{{ $t('Paid') }}</div>
              <h5 class="fw-bold mb-0" :class="{ 'text-info': paidAmount > 0 }">{{ $storeIndex.formatThaiCurrency(paidAmount) }}</h5>
            </div>
          </div>
        </div>
        <div class="col-4">
          <div class="card border-0 rounded-3 h-100" :class="remaining > 0 ? 'bg-danger bg-opacity-10' : 'bg-success bg-opacity-10'">
            <div class="card-body p-3 text-center">
              <div class="text-muted small mb-1">
                {{ change > 0 ? $t('Change') : $t('Remaining') }}
              </div>
              <h5 class="fw-bold mb-0" :class="change > 0 ? 'text-success' : (remaining > 0 ? 'text-danger' : 'text-success')">
                {{ $storeIndex.formatThaiCurrency(change > 0 ? change : remaining) }}
              </h5>
            </div>
          </div>
        </div>
      </div>

      <!-- No payment methods configured for this branch -->
      <div v-if="paymentMethods.length === 0" class="alert alert-warning rounded-3 mb-0">
        <i class="fa-solid fa-triangle-exclamation me-2"></i>
        {{ $t('No payment methods configured for this branch.') }}
      </div>

      <div v-else class="row g-3">

        <!-- Left Column -->
        <div class="col-md-7">
          <label class="form-label fw-bold small text-muted">
            <i class="fa-solid fa-credit-card me-1"></i> {{ $t('Payment Method') }}
          </label>
          <div class="row g-2 mb-3">
            <div class="col-4" v-for="method in paymentMethods" :key="method.id">
              <button
                type="button"
                class="btn w-100 py-2 rounded-3 fw-semibold d-flex flex-column align-items-center justify-content-center"
                :class="form.methodKey === method.id ? 'btn-theme text-white' : 'btn-outline-secondary'"
                :disabled="remaining <= 0 && change === 0"
                @click="selectMethod(method.id)"
              >
                <i :class="methodIcon(method)" class="fs-5 mb-1"></i>
                <small class="text-truncate w-100" :title="method.name">{{ $t(method.name) }}</small>
              </button>
            </div>
          </div>

          <!-- Cash UI -->
          <template v-if="selectedType === 'cash'">
            <div class="card border-0 bg-light rounded-3 mb-2">
              <div class="card-body p-2 px-3 d-flex justify-content-between align-items-center">
                <span class="small text-muted fw-semibold">
                  <i class="fa-solid fa-receipt me-1"></i> {{ $t('Amount Due') }}
                </span>
                <span class="fs-5 fw-bold">{{ $storeIndex.formatThaiCurrency(remaining) }}</span>
              </div>
            </div>

            <label class="form-label fw-bold small text-muted mt-1">
              <i class="fa-solid fa-money-bill-wave me-1"></i> {{ $t('Cash Received') }}
            </label>
            <div class="input-group input-group-lg mb-2">
              <span class="input-group-text bg-light fw-bold">฿</span>
              <input
                type="number"
                class="form-control form-control-lg fw-bold text-end shadow-none"
                style="font-size: 1.5rem;"
                v-model.number="form.cashReceived"
                placeholder="0.00"
                :disabled="remaining <= 0"
                min="0"
                step="1"
              >
            </div>

            <div class="mb-2">
              <div class="small text-muted mb-1">{{ $t('Quick Cash') }}</div>
              <div class="d-flex flex-wrap gap-1">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary rounded-pill px-3"
                  v-for="amt in quickAmounts"
                  :key="amt"
                  @click="form.cashReceived = amt"
                >
                  {{ $storeIndex.formatThaiCurrency(amt) }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-theme rounded-pill px-3 fw-semibold"
                  @click="form.cashReceived = parseFloat(remaining.toFixed(2))"
                >
                  <i class="fa-solid fa-equals me-1"></i> {{ $t('Exact') }}
                </button>
              </div>
            </div>

            <div
              class="card border-0 rounded-3 mb-3"
              :class="cashInsufficient ? 'bg-danger bg-opacity-10' : (changePreview > 0 ? 'bg-success bg-opacity-10 border border-success border-2' : 'bg-light')"
            >
              <div class="card-body p-3 d-flex justify-content-between align-items-center">
                <div>
                  <div class="small text-muted fw-semibold">
                    <i class="fa-solid fa-hand-holding-dollar me-1"></i>
                    {{ $t('Change to Return') }}
                  </div>
                  <div v-if="cashInsufficient" class="small text-danger fw-semibold mt-1">
                    <i class="fa-solid fa-triangle-exclamation me-1"></i>
                    {{ $t('Short by') }} {{ $storeIndex.formatThaiCurrency(Math.abs(changePreview)) }}
                  </div>
                  <div v-else-if="changePreview === 0 && form.cashReceived > 0" class="small text-success fw-semibold mt-1">
                    <i class="fa-solid fa-check me-1"></i>
                    {{ $t('Exact amount') }}
                  </div>
                </div>
                <div
                  class="fs-2 fw-bold"
                  :class="cashInsufficient ? 'text-danger' : (changePreview > 0 ? 'text-success' : 'text-muted')"
                >
                  {{ $storeIndex.formatThaiCurrency(Math.max(0, changePreview)) }}
                </div>
              </div>
            </div>
          </template>

          <!-- Non-Cash -->
          <template v-else-if="selectedMethod">
            <label class="form-label fw-bold small text-muted mt-2">
              <i class="fa-solid fa-coins me-1"></i> {{ $t('Amount') }}
            </label>
            <div class="input-group mb-2">
              <span class="input-group-text bg-light fw-bold">฿</span>
              <input
                type="number"
                class="form-control form-control-lg fw-bold text-end shadow-none"
                v-model.number="form.amount"
                placeholder="0.00"
                :disabled="remaining <= 0"
                min="0"
                step="0.01"
              >
            </div>
            <div class="mb-3">
              <button
                type="button"
                class="btn btn-sm btn-outline-theme rounded-pill px-3 fw-semibold"
                @click="form.amount = parseFloat(remaining.toFixed(2))"
                :disabled="remaining <= 0"
              >
                <i class="fa-solid fa-equals me-1"></i> {{ $t('Pay Remaining') }}
              </button>
            </div>

            <!-- PromptPay QR — shown when the selected method has a promptpay_no -->
            <div
              v-if="selectedMethod.promptpay_no"
              class="card border-0 rounded-3 mb-3"
              style="background: linear-gradient(135deg, #002d5f 0%, #0056b3 100%);"
            >
              <div class="card-body p-3 text-center text-white">
                <div class="fw-bold mb-2" style="letter-spacing: 1px;">PromptPay</div>
                <div class="bg-white rounded-3 p-2 d-inline-block">
                  <div v-if="qrDataUrl" class="position-relative" style="width: 220px; height: 220px;">
                    <img
                      :src="qrDataUrl"
                      alt="PromptPay QR"
                      class="img-fluid"
                      style="width: 220px; height: 220px;"
                    >
                    <img
                      :src="promptPayIcon"
                      alt="PromptPay"
                      class="position-absolute top-50 start-50 translate-middle"
                      style="width: 44px; height: 44px; object-fit: contain;"
                    >
                  </div>
                  <div v-else class="text-muted small d-flex align-items-center justify-content-center" style="width: 220px; height: 220px;">
                    <span class="px-2 text-center">{{ $t('Enter amount to generate QR') }}</span>
                  </div>
                </div>
                <div class="small mt-2 opacity-75">
                  <i class="fa-solid fa-phone me-1"></i>
                  {{ formatPromptPayId(selectedMethod.promptpay_no) }}
                </div>
                <div v-if="selectedMethod.account_name" class="small opacity-75">
                  <i class="fa-solid fa-user me-1"></i>
                  {{ selectedMethod.account_name }}
                </div>
              </div>
            </div>

            <!-- Bank Transfer info — shown for transfer methods without QR -->
            <div
              v-else-if="hasBankInfo(selectedMethod)"
              class="card border-0 bg-light rounded-3 mb-3"
            >
              <div class="card-body p-3">
                <div class="small text-muted fw-semibold mb-2">
                  <i class="fa-solid fa-building-columns me-1"></i> {{ $t('Bank Account') }}
                </div>
                <div v-if="selectedMethod.bank_name" class="d-flex justify-content-between small mb-1">
                  <span class="text-muted">{{ $t('Bank') }}</span>
                  <span class="fw-semibold">{{ selectedMethod.bank_name }}</span>
                </div>
                <div v-if="selectedMethod.account_no" class="d-flex justify-content-between small mb-1">
                  <span class="text-muted">{{ $t('Account No') }}</span>
                  <span class="fw-semibold font-monospace">{{ selectedMethod.account_no }}</span>
                </div>
                <div v-if="selectedMethod.account_name" class="d-flex justify-content-between small">
                  <span class="text-muted">{{ $t('Account Name') }}</span>
                  <span class="fw-semibold">{{ selectedMethod.account_name }}</span>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted fw-semibold">
                {{ selectedMethod.promptpay_no ? $t('Slip Reference') : $t('Reference / Last 4 digits') }}
              </label>
              <input
                type="text"
                class="form-control shadow-none"
                v-model="form.reference"
                :placeholder="$t('Optional')"
              >
            </div>
          </template>

        </div>

        <!-- Right Column -->
        <div class="col-md-5">
          <label class="form-label fw-bold small text-muted">
            <i class="fa-solid fa-list-ul me-1"></i> {{ $t('Added Payments') }} ({{ payments.length }})
          </label>

          <div v-if="payments.length === 0" class="card border-0 bg-light rounded-3">
            <div class="card-body text-center py-5 text-muted">
              <i class="fa-solid fa-wallet fa-2x opacity-50 mb-2"></i>
              <p class="small mb-0">{{ $t('No payment added yet') }}</p>
              <p class="small mb-0">{{ $t('Select a method and add amount') }}</p>
            </div>
          </div>

          <div v-else class="d-flex flex-column gap-2">
            <div
              v-for="(p, index) in payments"
              :key="index"
              class="card border-0 shadow-sm rounded-3"
            >
              <div class="card-body p-2 d-flex align-items-center">
                <div class="rounded-circle bg-theme bg-opacity-10 text-theme d-flex align-items-center justify-content-center me-2"
                  style="width: 38px; height: 38px;">
                  <i :class="p.icon"></i>
                </div>
                <div class="flex-grow-1" style="min-width: 0;">
                  <div class="fw-semibold text-truncate" style="font-size: 0.9rem;">{{ p.name }}</div>
                  <div v-if="p.type === 'cash' && p.change > 0" class="text-muted" style="font-size: 0.72rem; line-height: 1.3;">
                    {{ $t('Received') }}: {{ $storeIndex.formatThaiCurrency(p.cash_received) }}
                    <span class="text-success">• {{ $t('Change') }}: {{ $storeIndex.formatThaiCurrency(p.change) }}</span>
                  </div>
                  <div v-else-if="p.reference" class="text-muted text-truncate" style="font-size: 0.72rem;">
                    Ref: {{ p.reference }}
                  </div>
                </div>
                <div class="text-end me-2">
                  <div class="fw-bold text-theme">{{ $storeIndex.formatThaiCurrency(p.amount) }}</div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger p-0"
                  :disabled="submitting"
                  @click="removePayment(index)"
                >
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="isFullyPaid" class="alert alert-success mt-3 mb-0 py-2 text-center">
            <i class="fa-solid fa-circle-check me-1"></i>
            <span class="fw-semibold">{{ $t('Ready to confirm') }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top d-flex gap-2">
      <button type="button" class="btn btn-outline-secondary rounded-3 px-4 flex-fill" :disabled="submitting" @click="handleClose">
        <i class="fa-solid fa-arrow-left me-1"></i> {{ inline ? $t('Back') : $t('Cancel') }}
      </button>
      <ladda
        :text="primaryLabel"
        :btn-class="'btn rounded-3 px-4 fw-bold flex-fill ' + (primaryConfirms ? 'btn-success' : 'btn-theme')"
        :loading="submitting"
        :icon="primaryIcon"
        :disabled="primaryDisabled"
        @click="primaryAction"
      />
    </div>
  </div>
</template>

<script>
import Ladda from '@/plugins/Ladda.vue';
import QRCode from 'qrcode';
import { generatePromptPayPayload } from '@/utils/promptpay';
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  name: 'OffcanvasPayment',
  components: { Ladda },
  props: {
    sessionName: { type: String, default: '' },
    // Full bill object (active bill) — drives amounts, items, and the API call.
    bill: { type: Object, required: true },
    // Branch payment methods returned by GET /sessions/{token}/bills (root level).
    // Each: { id, payment_method_id, name, type, bank_name, account_no, account_name, promptpay_no }
    paymentMethods: { type: Array, default: () => [] },
    // Reserved for components that need to inspect the active session; the
    // actual payBill call lives in the parent so the prop is informational.
    sessionToken: { type: String, default: '' },
    inline: { type: Boolean, default: false },
    // Parent owns the API request; this drives the Ladda spinner / disabled states.
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      form: {
        methodKey: null,  // selected branch_payment_method.id
        amount: null,
        cashReceived: null,
        reference: ''
      },
      // Pending payments staged in this session — committed in one API call.
      payments: [],
      qrDataUrl: '',
      promptPayIcon: require('@/assets/images/icon_promptpay.svg'),
      quickAmounts: [20, 50, 100, 500, 1000]
    };
  },
  computed: {
    billNo() {
      return this.bill?.bill_no || '';
    },
    // The check-bill view recomputes grand_total locally as the cashier edits
    // discounts/shipping — trust that figure here.
    grandTotal() {
      return Number(this.bill?.grand_total) || 0;
    },
    selectedMethod() {
      return this.paymentMethods.find(m => m.id === this.form.methodKey) || null;
    },
    selectedType() {
      return this.selectedMethod?.type || '';
    },
    paidAmount() {
      return this.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    },
    remaining() {
      const rem = this.grandTotal - this.paidAmount;
      return rem > 0 ? rem : 0;
    },
    change() {
      return this.payments.reduce((sum, p) => sum + parseFloat(p.change || 0), 0);
    },
    isFullyPaid() {
      return this.paidAmount + 0.0001 >= this.grandTotal && this.payments.length > 0;
    },
    changePreview() {
      if (this.selectedType !== 'cash') return 0;
      const received = parseFloat(this.form.cashReceived || 0);
      if (received <= 0) return 0;
      return received - this.remaining;
    },
    cashInsufficient() {
      return this.selectedType === 'cash'
        && parseFloat(this.form.cashReceived || 0) > 0
        && this.changePreview < 0;
    },
    // PromptPay payload for the QR — only when the selected method has a
    // promptpay_no AND the user typed an amount.
    qrPayload() {
      if (!this.selectedMethod?.promptpay_no) return '';
      const amt = parseFloat(this.form.amount || 0);
      if (amt <= 0) return '';
      return generatePromptPayPayload(
        this.selectedMethod.promptpay_no,
        amt,
        this.selectedMethod.account_name || '',
        ''
      );
    },
    canAddPayment() {
      if (this.remaining <= 0) return false;
      if (!this.selectedMethod) return false;
      if (this.selectedType === 'cash') {
        return parseFloat(this.form.cashReceived || 0) > 0;
      }
      const amt = parseFloat(this.form.amount || 0);
      if (amt <= 0) return false;
      // Allow exact-or-less; backend re-checks the running total.
      if (amt > this.remaining + 0.001) return false;
      return true;
    },
    // How much the *current, un-staged* form entry would apply toward the bill
    // (cash is capped at remaining; the excess is change, not extra payment).
    pendingApplied() {
      if (!this.selectedMethod) return 0;
      const raw = this.selectedType === 'cash'
        ? parseFloat(this.form.cashReceived || 0)
        : parseFloat(this.form.amount || 0);
      if (!(raw > 0)) return 0;
      return Math.min(raw, this.remaining);
    },
    // Would committing the current entry (plus what's already staged) settle the
    // whole bill? Drives whether the single primary button confirms or just adds.
    willCoverAll() {
      return this.paidAmount + this.pendingApplied + 0.0001 >= this.grandTotal;
    },
    // The primary button confirms when the bill is (or is about to be) fully
    // covered; otherwise it just stages a partial split.
    primaryConfirms() {
      if (this.canAddPayment) return this.willCoverAll;
      return this.isFullyPaid;
    },
    primaryLabel() {
      return this.primaryConfirms ? this.$t('Confirm Payment') : this.$t('Add Payment');
    },
    primaryIcon() {
      return this.primaryConfirms
        ? "<i class='fa-solid fa-check-double'></i>"
        : "<i class='fa-solid fa-plus'></i>";
    },
    primaryDisabled() {
      if (this.submitting) return true;
      // Actionable when there's a valid entry to stage, or the bill is already
      // fully covered by staged payments.
      return !this.canAddPayment && !this.isFullyPaid;
    }
  },
  watch: {
    // Auto-pick the first available method on first load.
    paymentMethods: {
      immediate: true,
      handler(val) {
        if (this.form.methodKey == null && val && val.length > 0) {
          this.selectMethod(val[0].id);
        }
      }
    },
    qrPayload: {
      immediate: true,
      handler(payload) {
        if (!payload) { this.qrDataUrl = ''; return; }
        QRCode.toDataURL(payload, { margin: 1, width: 300, errorCorrectionLevel: 'H' }, (err, url) => {
          this.qrDataUrl = err ? '' : url;
        });
      }
    }
  },
  methods: {
    methodIcon(m) {
      if (!m) return 'fa-solid fa-money-bill';
      if (m.promptpay_no) return 'fa-solid fa-qrcode';
      if (m.type === 'cash') return 'fa-solid fa-money-bill-wave';
      if (m.type === 'transfer') return 'fa-solid fa-building-columns';
      const name = (m.name || '').toLowerCase();
      if (name.includes('credit') || name.includes('debit') || name.includes('card')) return 'fa-solid fa-credit-card';
      if (name.includes('wallet') || name.includes('pay')) return 'fa-solid fa-wallet';
      if (name.includes('voucher') || name.includes('coupon')) return 'fa-solid fa-ticket';
      return 'fa-solid fa-money-bill';
    },
    hasBankInfo(m) {
      return !!(m && (m.bank_name || m.account_no || m.account_name));
    },
    formatPromptPayId(id) {
      // Mask all but the last 4 digits to avoid printing the full number on screen.
      const digits = String(id || '').replace(/\D/g, '');
      if (digits.length <= 4) return digits;
      return 'xxx-xxx-' + digits.slice(-4);
    },
    selectMethod(id) {
      this.form.methodKey = id;
      this.form.reference = '';
      this.form.amount = null;
      this.form.cashReceived = null;
      // Pre-fill the amount with the remaining balance so the common
      // "pay in full, one tap" case needs no typing. Cash defaults to the
      // exact amount; the cashier overrides it when more cash is handed over.
      if (this.remaining > 0) {
        if (this.selectedType === 'cash') {
          this.form.cashReceived = parseFloat(this.remaining.toFixed(2));
        } else {
          this.form.amount = parseFloat(this.remaining.toFixed(2));
        }
      }
    },
    addPayment() {
      if (!this.canAddPayment || !this.selectedMethod) return;
      const m = this.selectedMethod;
      if (m.type === 'cash') {
        const received = parseFloat(this.form.cashReceived);
        const applied = Math.min(received, this.remaining);
        const change = Math.max(0, received - this.remaining);
        this.payments.push({
          id: m.id,
          payment_method_id: m.payment_method_id,
          name: m.name,
          type: m.type,
          icon: this.methodIcon(m),
          amount: parseFloat(applied.toFixed(2)),
          cash_received: parseFloat(received.toFixed(2)),
          change: parseFloat(change.toFixed(2)),
          reference: null
        });
      } else {
        let amt = parseFloat(this.form.amount);
        if (amt > this.remaining) amt = this.remaining;
        this.payments.push({
          id: m.id,
          payment_method_id: m.payment_method_id,
          name: m.name,
          type: m.type,
          icon: this.methodIcon(m),
          amount: parseFloat(amt.toFixed(2)),
          reference: this.form.reference || null
        });
      }
      // Reset form, falling back to the first cash method if any remains owed.
      this.form.amount = null;
      this.form.cashReceived = null;
      this.form.reference = '';
      if (this.remaining > 0) {
        const cash = this.paymentMethods.find(x => x.type === 'cash');
        if (cash) this.selectMethod(cash.id);
      }
    },
    removePayment(index) {
      this.payments.splice(index, 1);
    },
    handleClose() {
      if (this.inline) {
        this.$emit('back');
      } else {
        $(this.$refs.offcanvas).offcanvas('hide');
      }
    },
    // Programmatic close (non-inline) — lets the parent dismiss the offcanvas
    // after a successful payment so Bootstrap removes its backdrop too.
    hide() {
      if (this.inline) {
        this.$emit('back');
      } else {
        $(this.$refs.offcanvas).offcanvas('hide');
      }
    },
    buildApiBody() {
      // payment[] — required, ≥1; uses branch_payment_methods.id as `id`.
      const payment = this.payments.map(p => ({
        id: p.id,
        payment_method_id: p.payment_method_id,
        amount: parseFloat(parseFloat(p.amount).toFixed(2))
      }));

      // items[] — overwrite-style. Snap backend to current UI for every active
      // (non-cancelled) item so any in-flight discount edits in the check-bill
      // view get persisted in the same transaction.
      const items = (this.bill?.items || [])
        .filter(it => !it.cancelled && it._source_id != null)
        .map(it => ({
          id: it._source_id,
          discount: parseFloat((parseFloat(it.discount) || 0).toFixed(2))
        }));

      const body = { payment };
      if (items.length > 0) body.items = items;
      // Always include bill-level discount so user edits land too; on a fresh
      // bill this is a no-op (re-applies the same value).
      body.discount = parseFloat((parseFloat(this.bill?.discount) || 0).toFixed(2));
      // Bill-level shipping — backend field name is `shipping`, internal field
      // is `shipping_price` (kept aligned with the GET bills response). Same
      // overwrite semantics as `discount`: sending the current value persists
      // any edits made in the check-bill view; sending 0 clears it.
      body.shipping = parseFloat((parseFloat(this.bill?.shipping_price) || 0).toFixed(2));
      return body;
    },
    handleConfirm() {
      if (!this.isFullyPaid || this.submitting) return;
      this.$emit('confirmed', this.buildApiBody());
    },
    // Single smart action behind the footer button:
    //  - stages the current entry if it's valid, then
    //  - confirms immediately when the bill is fully covered, otherwise
    //    stays open so the cashier can add the next split.
    primaryAction() {
      if (this.submitting) return;
      if (this.canAddPayment) this.addPayment();
      if (this.isFullyPaid) this.handleConfirm();
    }
  },
  mounted() {
    if (this.inline) return;
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
  }
};
</script>

<style scoped>
.btn-outline-theme {
  color: var(--bs-primary, #e17055);
  border-color: var(--bs-primary, #e17055);
}
.btn-outline-theme:hover {
  background-color: var(--bs-primary, #e17055);
  color: #fff;
}
</style>
